import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack,
  CloudUploadOutlined,
  Delete,
  ImageOutlined,
} from '@mui/icons-material';

import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { UserContext } from '../../components/shared/useUser';

import useNswagClient from '../../hooks/api/useNswagClient';
import useRecipeV2ComponentSearch from './hooks/useRecipeV2ComponentSearch';
import { StockItemWithRecipeFlag } from '../Recipe/hooks/useStockItemsData';

import useRecipeV2Api from './hooks/useRecipeV2Api';

import {
  CreateRecipeRequest,
  ProductionMethod,
  RecipeComponent,
  UpdateRecipeMetadataRequest,
} from './types';

import { prepareRecipeImage } from './recipeMediaUpload';

const STEPS = [
  'create.step1',
  'create.step2',
  'create.step3',
] as const;

const noSpinnerSx = {
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
};

type ComponentDraft = {
    id: number;
    componentProductId: number;
    componentItemNumber: string;
    componentDescription: string;
    quantity: number;
    weight: number | null;
    volume: number | null;
    uom: string;
};

const toComponentDraft = (component: RecipeComponent): ComponentDraft => ({
  id: component.id,
  componentProductId: component.componentProductId,
  componentItemNumber: component.componentItemNumber,
  componentDescription: component.componentDescription,
  quantity: component.quantity,
  weight: component.weightSource === 'Authored' ? component.weight : null,
  volume: component.volumeSource === 'Authored' ? component.volume : null,
  uom: component.uom,
});

type ComponentPersistenceApi = Pick<
ReturnType<typeof useRecipeV2Api>,
'updateComponent' | 'getRecipeDetail'
>;

export const persistComponentDraft = async (
  api: ComponentPersistenceApi,
  recipeId: number,
  component: ComponentDraft,
  persistedFallback: ComponentDraft,
): Promise<{ component: ComponentDraft; error: string | null }> => {
  try {
    const saved = await api.updateComponent(recipeId, component.id, {
      componentProductId: component.componentProductId,
      quantity: component.quantity,
      weight: component.weight,
      volume: component.volume,
      uom: component.uom,
      sortOrder: 0,
    });

    return {
      component: toComponentDraft(saved),
      error: null,
    };
  } catch (error) {
    let restored = persistedFallback;

    try {
      const recipe = await api.getRecipeDetail(recipeId);
      const persisted = recipe.components?.find(
        (candidate) => candidate.id === component.id,
      );

      if (persisted) {
        restored = toComponentDraft(persisted);
      }
    } catch {
      // The last confirmed persisted snapshot remains the safe fallback.
    }

    return {
      component: restored,
      error: error instanceof Error
        ? error.message
        : 'Failed to update component.',
    };
  }
};

const RecipeV2CreatePage = () => {
  const { t } = useTranslation('recipeV2');
  const navigate = useNavigate();
  const theme = useTheme();

  const api = useRecipeV2Api();
  const nswagApi = useNswagClient();

  const {
    selectedFranchise,
    selectedStore,
  } = useContext(UserContext);

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  /*
     * Step 1
     */
  const recipeType = 'Recipe';

  const [description, setDescription] = useState('');
  const [narrativeDescription, setNarrativeDescription] = useState('');
  const [itemNumber, setItemNumber] = useState('');
  const [primaryCategory, setPrimaryCategory] = useState('');

  const [yieldQuantity, setYieldQuantity] =
        useState<number | ''>('');

  const [portionQuantity, setPortionQuantity] =
        useState<number | ''>('');

  const [portionUom, setPortionUom] = useState('');

  const [expectedYieldLossPercent, setExpectedYieldLossPercent] =
        useState<number | ''>('');

  const [consumptionMode, setConsumptionMode] =
        useState<'PerPortion' | 'WholeYield' | 'ProducedStock'>('PerPortion');

  const [productionTrigger, setProductionTrigger] =
        useState<'Automatic' | 'Manual' | ''>('');

  const [storageMethod, setStorageMethod] = useState('');

  const [shelfLifeValue, setShelfLifeValue] =
        useState<number | ''>('');

  const [shelfLifeUnit, setShelfLifeUnit] = useState('');

  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');

  const [productionMethodId, setProductionMethodId] =
        useState<number | ''>('');

  /*
     * Recipe is first created as Draft when Step 1 -> Next.
     *
     * Going back and forward updates the same Draft.
     */
  const [recipeId, setRecipeId] =
        useState<number | null>(null);

  const [categories, setCategories] =
        useState<string[]>([]);

  const [productionMethods, setProductionMethods] =
        useState<ProductionMethod[]>([]);

  /*
     * Hero image
     */
  const [heroImageFile, setHeroImageFile] =
        useState<File | null>(null);

  const [heroImagePreviewUrl, setHeroImagePreviewUrl] =
        useState<string | null>(null);

  const [hasPersistedHeroImage, setHasPersistedHeroImage] =
        useState(false);

  const heroImagePreviewRef =
        useRef<string | null>(null);

  const heroImageInputRef =
        useRef<HTMLInputElement | null>(null);

  /*
     * Step 2 components
     */
  const {
    stockItems,
    setStockItems,
    recipeItems,
    isSearching,
    debouncedSearch,
  } = useRecipeV2ComponentSearch();

  const [draftComponents, setDraftComponents] =
        useState<ComponentDraft[]>([]);

  const persistedComponentsRef =
        useRef<Record<number, ComponentDraft>>({});

  const [pendingComponentSaveIds, setPendingComponentSaveIds] =
        useState<Set<number>>(() => new Set());

  const [selectedStockItem, setSelectedStockItem] =
        useState<StockItemWithRecipeFlag | null>(null);

  const [componentSearchInput, setComponentSearchInput] =
        useState('');

  const [tempValues, setTempValues] =
        useState<Record<string, string>>({});

  const releaseHeroImagePreview = () => {
    if (heroImagePreviewRef.current) {
      URL.revokeObjectURL(
        heroImagePreviewRef.current,
      );
    }

    heroImagePreviewRef.current = null;
  };

  useEffect(() => {
    return () => {
      releaseHeroImagePreview();
    };
  }, []);

  const selectHeroImage = (file: File) => {
    if (
      ![
        'image/jpeg',
        'image/png',
        'image/webp',
      ].includes(file.type)
    ) {
      setMutationError(
        'Select a JPEG, PNG, or WebP image.',
      );

      return;
    }

    releaseHeroImagePreview();

    const previewUrl =
            URL.createObjectURL(file);

    heroImagePreviewRef.current =
            previewUrl;

    setHeroImageFile(file);
    setHeroImagePreviewUrl(previewUrl);
    setMutationError(null);
  };

  const removeHeroImage = async () => {
    setMutationError(null);

    if (
      recipeId
            && hasPersistedHeroImage
    ) {
      try {
        await api.removeRecipeMedia(
          recipeId,
        );

        setHasPersistedHeroImage(false);
      } catch (error) {
        setMutationError(
          error instanceof Error
            ? error.message
            : 'Failed to remove recipe image.',
        );

        return;
      }
    }

    releaseHeroImagePreview();

    setHeroImageFile(null);
    setHeroImagePreviewUrl(null);
  };

  /*
     * Load metadata.
     *
     * IMPORTANT:
     * Do not add api or nswagApi to this dependency array.
     * useNswagClient creates a new object each render and would
     * repeatedly execute this effect.
     */
  useEffect(() => {
    if (selectedStore?.storeNumber) {
      nswagApi
        .getPrimaryCategoriesForStore(
          selectedStore.storeNumber,
        )
        .then((response) => {
          const names = (
            response?.data ?? []
          )
            .map(
              (category: {
                                primaryCategoryName?: string;
                            }) =>
                category.primaryCategoryName
                                ?? '',
            )
            .filter(Boolean)
            .sort(
              (a, b) =>
                a.localeCompare(b),
            );

          setCategories(names);
        })
        .catch(() => {
          setCategories([]);
        });
    } else {
      setCategories([]);
    }

    api
      .getProductionMethods(
        selectedFranchise?.franchiseId,
      )
      .then(setProductionMethods)
      .catch(() => {
        setProductionMethods([]);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedStore?.storeNumber,
    selectedFranchise?.franchiseId,
  ]);

  const onComponentInputChange = (
    _event: React.SyntheticEvent,
    value: string,
  ) => {
    setComponentSearchInput(value);

    if (selectedStore?.storeNumber) {
      debouncedSearch(
        value,
        selectedStore.storeNumber,
      );
    }
  };

  const handleAddComponent = async (
    item: StockItemWithRecipeFlag | null,
  ) => {
    setSelectedStockItem(null);
    setComponentSearchInput('');

    if (
      !item
            || !recipeId
            || item.id == null
    ) {
      return;
    }

    const componentProductId =
            item.id;

    const quantity = 1;
    const weight: number | null = null;
    const volume: number | null = null;

    try {
      setMutationError(null);

      const created =
                await api.addComponent(
                  recipeId,
                  {
                    componentProductId,

                    componentItemNumber:
                            (
                              item.itemNumber
                                ?? ''
                            ).toString(),

                    componentDescription:
                            item.description
                            ?? '',

                    quantity,
                    weight,
                    volume,

                    uom:
                            item.uom ?? '',
                  },
                );

      const persistedComponent = toComponentDraft(created);
      persistedComponentsRef.current[created.id] = persistedComponent;

      setDraftComponents(
        (previous) => [
          ...previous,
          persistedComponent,
        ],
      );

      setStockItems(
        stockItems.filter(
          (stockItem) =>
            stockItem.id
                        !== item.id,
        ),
      );
    } catch (error) {
      setMutationError(
        error instanceof Error
          ? error.message
          : 'Failed to add component.',
      );
    }
  };

  const handleRemoveComponent = async (
    id: number,
  ) => {
    if (!recipeId) return;

    try {
      setMutationError(null);

      await api.removeComponent(
        recipeId,
        id,
      );

      delete persistedComponentsRef.current[id];

      setDraftComponents(
        (previous) =>
          previous.filter(
            (component) =>
              component.id
                            !== id,
          ),
      );
    } catch (error) {
      setMutationError(
        error instanceof Error
          ? error.message
          : 'Failed to remove component.',
      );
    }
  };

  /*
     * Quantity/UOM is the authored composition source of truth.
     * Weight and volume are optional independent physical facts and are never
     * reverse-calculated in the browser.
     */
  const handleComponentFieldChange = (
    id: number,
    field: 'weight' | 'quantity' | 'volume',
    value: string,
  ) => {
    setTempValues(
      (previous) => ({
        ...previous,
        [`${id}-${field}`]: value,
      }),
    );

    setDraftComponents(
      (previous) =>
        previous.map((component) => {
          if (component.id !== id) {
            return component;
          }

          if (field === 'quantity') {
            return {
              ...component,
              quantity: value === '' ? 0 : Number(value),
            };
          }

          return {
            ...component,
            [field]: value === '' ? null : Number(value),
          };
        }),
    );
  };

  const handleComponentFieldBlur = async (
    id: number,
  ) => {
    setTempValues(
      (previous) => {
        const next = {
          ...previous,
        };

        delete next[`${id}-quantity`];
        delete next[`${id}-weight`];
        delete next[`${id}-volume`];

        return next;
      },
    );

    if (!recipeId) return;

    const component =
            draftComponents.find(
              (draftComponent) =>
                draftComponent.id
                    === id,
            );

    if (!component) return;

    const persistedFallback =
      persistedComponentsRef.current[id]
      ?? component;

    setPendingComponentSaveIds((current) => {
      const next = new Set(current);
      next.add(id);
      return next;
    });

    const result = await persistComponentDraft(
      api,
      recipeId,
      component,
      persistedFallback,
    );

    persistedComponentsRef.current[id] = result.component;

    setDraftComponents((previous) =>
      previous.map((candidate) =>
        candidate.id === id
          ? result.component
          : candidate,
      ),
    );

    setMutationError(result.error);

    setPendingComponentSaveIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  };

  const isStep1Valid =
        description.trim() !== ''
        && itemNumber.trim() !== ''
        && primaryCategory.trim() !== ''
        && (yieldQuantity === '' || yieldQuantity > 0)
        && (
          (portionQuantity === '' && portionUom.trim() === '')
          || (portionQuantity !== '' && portionQuantity > 0 && portionUom.trim() !== '')
        )
        && (expectedYieldLossPercent === ''
          || (expectedYieldLossPercent >= 0 && expectedYieldLossPercent <= 100))
        && (
          consumptionMode !== 'ProducedStock'
          || (
            productionTrigger !== ''
            && storageMethod.trim() !== ''
            && shelfLifeValue !== ''
            && shelfLifeValue > 0
            && shelfLifeUnit.trim() !== ''
          )
        );

  const uploadHeroImage = async (
    id: number,
    file: File,
  ) => {
    const prepared =
            await prepareRecipeImage(
              file,
            );

    const upload =
            await api.authorizeRecipeMediaUpload(
              id,
              prepared,
            );

    const response = await fetch(
      upload.uploadUrl,
      {
        method: 'PUT',

        headers: {
          'x-ms-blob-type':
                        'BlockBlob',

          'Content-Type':
                        prepared.type,
        },

        body:
                    prepared,
      },
    );

    if (!response.ok) {
      throw new Error(
        'Image upload to storage failed.',
      );
    }

    await api.finalizeRecipeMediaUpload(
      id,
      upload.uploadId,
    );

    setHasPersistedHeroImage(
      true,
    );
  };

  /*
     * Step 1 -> Next
     *
     * First time:
     *   create Draft recipe
     *
     * Subsequent time:
     *   update same Draft
     */
  const handleStep1Next = async () => {
    if (
      !selectedFranchise?.franchiseId
            || !isStep1Valid
    ) {
      return;
    }

    setSaving(true);
    setMutationError(null);

    try {
      const yieldPortions =
                yieldQuantity === ''
                  ? null
                  : yieldQuantity;

      const operationalConfiguration = {
        yieldPortions,
        portionQuantity:
          portionQuantity === ''
            ? null
            : portionQuantity,
        portionUom:
          portionQuantity === ''
            ? null
            : portionUom.trim() || null,
        expectedYieldLossPercent:
          expectedYieldLossPercent === ''
            ? null
            : expectedYieldLossPercent,
        consumptionMode,
        productionTrigger:
          consumptionMode === 'ProducedStock' && productionTrigger !== ''
            ? productionTrigger
            : null,
        storageMethod:
          consumptionMode === 'ProducedStock'
            ? storageMethod.trim() || null
            : null,
        shelfLifeValue:
          consumptionMode === 'ProducedStock' && shelfLifeValue !== ''
            ? shelfLifeValue
            : null,
        shelfLifeUnit:
          consumptionMode === 'ProducedStock'
            ? shelfLifeUnit.trim() || null
            : null,
      };

      let savedRecipeId =
                recipeId;

      if (
        savedRecipeId === null
      ) {
        const request:
                    CreateRecipeRequest = {
                      franchiseId:
                        selectedFranchise
                          .franchiseId,

                      description,

                      narrativeDescription:
                        narrativeDescription
                        || null,

                      itemNumber,

                      primaryCategory,

                      effectiveFrom:
                        effectiveFrom
                        || undefined,

                      effectiveTo:
                        effectiveTo
                        || undefined,

                      productionMethodId:
                        productionMethodId
                            === ''
                          ? null
                          : productionMethodId,

                      ...operationalConfiguration,
                    };

        const created =
                    await api.createRecipe(
                      request,
                    );

        savedRecipeId =
                    created.id;

        setRecipeId(
          created.id,
        );
      } else {
        const request:
                    UpdateRecipeMetadataRequest = {
                      description,

                      primaryCategory,

                      narrativeDescription:
                        narrativeDescription
                        || null,

                      ...operationalConfiguration,

                      effectiveFrom:
                        effectiveFrom
                        || undefined,

                      effectiveTo:
                        effectiveTo
                        || undefined,

                      productionMethodId:
                        productionMethodId
                            === ''
                          ? null
                          : productionMethodId,
                    };

        await api.updateRecipe(
          savedRecipeId,
          request,
        );
      }

      /*
             * Recipe must exist before media can be uploaded.
             */
      if (
        heroImageFile
                && savedRecipeId
      ) {
        try {
          await uploadHeroImage(
            savedRecipeId,
            heroImageFile,
          );

          /*
                     * Keep the preview URL for the Review step,
                     * but don't upload the same File again.
                     */
          setHeroImageFile(null);
        } catch (error) {
          setMutationError(
            error instanceof Error
              ? `Recipe was saved, but the image could not be uploaded. ${error.message}`
              : 'Recipe was saved, but the image could not be uploaded. Retry the image upload.',
          );

          return;
        }
      }

      setActiveStep(1);
    } catch (error) {
      setMutationError(
        error instanceof Error
          ? error.message
          : 'Failed to save recipe.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = async () => {
    if (!recipeId) return;

    setSaving(true);
    setMutationError(null);

    try {
      await api.finishRecipe(
        recipeId,
      );

      navigate(
        `/range/recipes/${recipeId}`,
      );
    } catch (error) {
      setMutationError(
        error instanceof Error
          ? error.message
          : 'Failed to publish recipe.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageArea>
      <Stack
        spacing={3}
        width="100%"
      >
        {mutationError && (
          <Alert severity="error">
            {mutationError}
          </Alert>
        )}

        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            minWidth: 0,
          }}
        >
          <IconButton
            onClick={() =>
              navigate(
                '/range/recipes',
              )
            }
            size="small"
          >
            <ArrowBack />
          </IconButton>

          <PageTitle>
            {t(
              'create.title',
            )}
          </PageTitle>
        </Stack>

        {/* Steps */}
        <Box
          sx={{
            overflowX: 'auto',
            pb: 1,
          }}
        >
          <Stepper
            activeStep={
              activeStep
            }
            sx={{
              pb: 2,
              minWidth: 460,
            }}
          >
            {STEPS.map(
              (label) => (
                <Step
                  key={label}
                >
                  <StepLabel>
                    {t(label)}
                  </StepLabel>
                </Step>
              ),
            )}
          </Stepper>
        </Box>

        {/* Step 1 */}
        {activeStep === 0 && (
          <Section
            style={{
              p: {
                xs: 2,
                sm: 5,
              },
            }}
          >
            <Grid
              container
              spacing={{
                xs: 2,
                sm: 4,
              }}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  label={t(
                    'create.description',
                  )}
                  placeholder={t(
                    'create.descriptionPlaceholder',
                  )}
                  value={
                    description
                  }
                  onChange={(
                    event,
                  ) =>
                    setDescription(
                      event
                        .target
                        .value,
                    )
                  }
                  required
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  label={t(
                    'create.itemNumber',
                  )}
                  placeholder={t(
                    'create.itemNumberPlaceholder',
                  )}
                  value={
                    itemNumber
                  }
                  onChange={(
                    event,
                  ) =>
                    setItemNumber(
                      event
                        .target
                        .value,
                    )
                  }
                  required
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
              >
                <TextField
                  label="Narrative description"
                  value={
                    narrativeDescription
                  }
                  onChange={(
                    event,
                  ) =>
                    setNarrativeDescription(
                      event
                        .target
                        .value,
                    )
                  }
                  multiline
                  minRows={3}
                  inputProps={{
                    maxLength: 4000,
                  }}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  label={t(
                    'create.category',
                  )}
                  select
                  value={
                    primaryCategory
                  }
                  onChange={(
                    event,
                  ) =>
                    setPrimaryCategory(
                      event
                        .target
                        .value,
                    )
                  }
                  required
                  fullWidth
                >
                  <MenuItem
                    value=""
                    disabled
                  >
                    <em>
                      {t(
                        'create.categoryPlaceholder',
                      )}
                    </em>
                  </MenuItem>

                  {categories.map(
                    (
                      category,
                    ) => (
                      <MenuItem
                        key={
                          category
                        }
                        value={
                          category
                        }
                      >
                        {
                          category
                        }
                      </MenuItem>
                    ),
                  )}
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  label="Yield portions"
                  type="number"
                  value={yieldQuantity}
                  onChange={(event) =>
                    setYieldQuantity(
                      event.target.value === ''
                        ? ''
                        : Number(event.target.value),
                    )
                  }
                  inputProps={{
                    min: 0.01,
                    step: 'any',
                  }}
                  sx={noSpinnerSx}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  label="Portion quantity"
                  type="number"
                  value={portionQuantity}
                  onChange={(event) => {
                    const value = event.target.value === ''
                      ? ''
                      : Number(event.target.value);
                    setPortionQuantity(value);
                    if (value === '') {
                      setPortionUom('');
                    }
                  }}
                  inputProps={{
                    min: 0.01,
                    step: 'any',
                  }}
                  sx={noSpinnerSx}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  label="Portion UOM"
                  value={portionUom}
                  onChange={(event) => setPortionUom(event.target.value)}
                  disabled={portionQuantity === ''}
                  helperText={portionQuantity === '' ? 'Optional - set a portion quantity first.' : undefined}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  label="Expected yield loss %"
                  type="number"
                  value={expectedYieldLossPercent}
                  onChange={(event) =>
                    setExpectedYieldLossPercent(
                      event.target.value === ''
                        ? ''
                        : Number(event.target.value),
                    )
                  }
                  inputProps={{
                    min: 0,
                    max: 100,
                    step: 'any',
                  }}
                  sx={noSpinnerSx}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  label="Consumption mode"
                  select
                  value={consumptionMode}
                  onChange={(event) => {
                    const next = event.target.value as 'PerPortion' | 'WholeYield' | 'ProducedStock';
                    setConsumptionMode(next);
                    if (next !== 'ProducedStock') {
                      setProductionTrigger('');
                      setStorageMethod('');
                      setShelfLifeValue('');
                      setShelfLifeUnit('');
                    }
                  }}
                  fullWidth
                >
                  <MenuItem value="PerPortion">Per portion</MenuItem>
                  <MenuItem value="WholeYield">Whole yield</MenuItem>
                  <MenuItem value="ProducedStock">Produced stock</MenuItem>
                </TextField>
              </Grid>

              {consumptionMode === 'ProducedStock' && (
                <>
                  <Grid
                    item
                    xs={12}
                    md={3}>
                    <TextField
                      label="Production trigger"
                      select
                      value={productionTrigger}
                      onChange={(event) =>
                        setProductionTrigger(event.target.value as 'Automatic' | 'Manual' | '')
                      }
                      required
                      fullWidth
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Automatic">Automatic</MenuItem>
                      <MenuItem value="Manual">Manual</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={3}>
                    <TextField
                      label="Storage method"
                      value={storageMethod}
                      onChange={(event) => setStorageMethod(event.target.value)}
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={3}>
                    <TextField
                      label="Shelf life"
                      type="number"
                      value={shelfLifeValue}
                      onChange={(event) =>
                        setShelfLifeValue(
                          event.target.value === ''
                            ? ''
                            : Number(event.target.value),
                        )
                      }
                      inputProps={{ min: 0.01, step: 'any' }}
                      sx={noSpinnerSx}
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={3}>
                    <TextField
                      label="Shelf-life unit"
                      value={shelfLifeUnit}
                      onChange={(event) => setShelfLifeUnit(event.target.value)}
                      required
                      fullWidth
                    />
                  </Grid>
                </>
              )}

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  label={t(
                    'create.productionMethod',
                    'Production Method',
                  )}
                  select
                  value={
                    productionMethodId
                  }
                  onChange={(
                    event,
                  ) =>
                    setProductionMethodId(
                      event
                        .target
                        .value
                                                === ''
                        ? ''
                        : Number(
                          event
                            .target
                            .value,
                        ),
                    )
                  }
                  fullWidth
                >
                  <MenuItem value="">
                                        None
                  </MenuItem>

                  {productionMethods.map(
                    (
                      productionMethod,
                    ) => (
                      <MenuItem
                        key={
                          productionMethod.id
                        }
                        value={
                          productionMethod.id
                        }
                      >
                        {
                          productionMethod.name
                        }
                      </MenuItem>
                    ),
                  )}
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  label={t(
                    'create.effectiveFrom',
                    'Effective From',
                  )}
                  type="date"
                  value={
                    effectiveFrom
                  }
                  onChange={(
                    event,
                  ) =>
                    setEffectiveFrom(
                      event
                        .target
                        .value,
                    )
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  label={t(
                    'create.effectiveTo',
                    'Effective To',
                  )}
                  type="date"
                  value={
                    effectiveTo
                  }
                  onChange={(
                    event,
                  ) =>
                    setEffectiveTo(
                      event
                        .target
                        .value,
                    )
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                />
              </Grid>

              {/* Hero image */}
              <Grid
                item
                xs={12}
              >
                <Stack
                  spacing={1}
                >
                  <Typography
                    variant="textSM"
                    fontWeight={
                      600
                    }
                  >
                                        Recipe image
                  </Typography>

                  <input
                    ref={
                      heroImageInputRef
                    }
                    type="file"
                    aria-label="Choose recipe image"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={
                      saving
                    }
                    style={{
                      display:
                                                'none',
                    }}
                    onChange={(
                      event,
                    ) => {
                      const file =
                                                event
                                                  .target
                                                  .files?.[0];

                      if (file) {
                        selectHeroImage(
                          file,
                        );
                      }

                      event.currentTarget.value =
                                                '';
                    }}
                  />

                  {heroImagePreviewUrl
                    ? (
                      <Box
                        sx={{
                          position:
                                                        'relative',
                          borderRadius:
                                                        1,
                          overflow:
                                                        'hidden',
                        }}
                      >
                        <Box
                          component="img"
                          src={
                            heroImagePreviewUrl
                          }
                          alt="Recipe preview"
                          sx={{
                            width:
                                                            '100%',
                            height:
                                                            180,
                            display:
                                                            'block',
                            objectFit:
                                                            'cover',
                            bgcolor:
                                                            'action.hover',
                          }}
                        />

                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            position:
                                                            'absolute',
                            top: 8,
                            right: 8,
                          }}
                        >
                          <IconButton
                            size="small"
                            aria-label="Replace recipe image"
                            disabled={
                              saving
                            }
                            onClick={() =>
                              heroImageInputRef
                                .current
                                ?.click()
                            }
                            sx={{
                              bgcolor:
                                                                'rgba(255, 255, 255, 0.9)',
                              '&:hover':
                                                            {
                                                              bgcolor:
                                                                    'common.white',
                                                            },
                            }}
                          >
                            <CloudUploadOutlined fontSize="small" />
                          </IconButton>

                          <IconButton
                            size="small"
                            aria-label="Remove recipe image"
                            color="error"
                            disabled={
                              saving
                            }
                            onClick={
                              removeHeroImage
                            }
                            sx={{
                              bgcolor:
                                                                'rgba(255, 255, 255, 0.9)',
                              '&:hover':
                                                            {
                                                              bgcolor:
                                                                    'common.white',
                                                            },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Box>
                    )
                    : (
                      <Box
                        component="button"
                        type="button"
                        disabled={
                          saving
                        }
                        onClick={() =>
                          heroImageInputRef
                            .current
                            ?.click()
                        }
                        sx={{
                          minHeight:
                                                        144,
                          width:
                                                        '100%',
                          border:
                                                        '2px dashed',
                          borderColor:
                                                        'divider',
                          borderRadius:
                                                        1,
                          bgcolor:
                                                        'action.hover',
                          display:
                                                        'flex',
                          flexDirection:
                                                        'column',
                          alignItems:
                                                        'center',
                          justifyContent:
                                                        'center',
                          gap: 0.5,
                          color:
                                                        'text.secondary',
                          cursor:
                                                        saving
                                                          ? 'default'
                                                          : 'pointer',
                          '&:hover':
                                                        saving
                                                          ? undefined
                                                          : {
                                                            borderColor:
                                                                    'primary.main',
                                                            bgcolor:
                                                                    'action.selected',
                                                          },
                        }}
                      >
                        <ImageOutlined
                          sx={{
                            fontSize:
                                                            32,
                            color:
                                                            'text.disabled',
                          }}
                        />

                        <Typography
                          variant="textSM"
                          fontWeight={
                            600
                          }
                        >
                                                    Add a recipe image
                        </Typography>

                        <Typography variant="textXS">
                                                    JPEG, PNG, or WebP
                        </Typography>
                      </Box>
                    )}
                </Stack>
              </Grid>
            </Grid>
          </Section>
        )}

        {/* Step 2 */}
        {activeStep === 1 && (
          <Section
            style={{
              p: {
                xs: 2,
                sm: 5,
              },
            }}
          >
            <Stack spacing={3}>
              <Autocomplete
                fullWidth
                multiple={false}
                clearOnBlur
                options={[
                  ...stockItems.filter(
                    (item) =>
                      item.itemNumber
                                            !== itemNumber,
                  ),
                  ...recipeItems.filter(
                    (item) =>
                      item.itemNumber
                                            !== itemNumber,
                  ),
                ]}
                filterOptions={(
                  options,
                ) =>
                  options
                }
                loading={
                  isSearching
                }
                inputValue={
                  componentSearchInput
                }
                onInputChange={
                  onComponentInputChange
                }
                value={
                  selectedStockItem
                }
                onChange={(
                  _,
                  value,
                ) =>
                  handleAddComponent(
                    value,
                  )
                }
                getOptionKey={(
                  option,
                ) =>
                  `${option.isRecipe
                    ? 'r'
                    : 'i'
                  }-${option.id
                                    ?? ''
                  }`
                }
                getOptionLabel={(
                  option,
                ) =>
                  option
                    ? `${option.itemNumber} - ${option.description}`
                    : ''
                }
                disabled={
                  !selectedStore
                    ?.storeNumber
                }
                renderOption={(
                  props,
                  option,
                ) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display:
                                                'flex',
                      justifyContent:
                                                'space-between',
                      alignItems:
                                                'center',
                    }}
                  >
                    <span>
                      {
                        option.itemNumber
                      }
                      {' - '}
                      {
                        option.description
                      }
                    </span>

                    {option.isRecipe
                                            && (
                                              <Box
                                                component="span"
                                                sx={{
                                                  ml: 1,
                                                  fontSize:
                                                            '11px',
                                                  color:
                                                            theme
                                                              .palette
                                                              .custom
                                                              .gray[600],
                                                }}
                                              >
                                                {t(
                                                  'build.subRecipe',
                                                  'Sub-recipe',
                                                )}
                                              </Box>
                                            )}
                  </Box>
                )}
                renderInput={(
                  params,
                ) => (
                  <TextField
                    {...params}
                    label={t(
                      'build.searchComponents',
                    )}
                    fullWidth
                  />
                )}
              />

              {draftComponents.length > 0 && (
                <TableContainer
                  sx={{
                    overflowX:
                                            'auto',
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      minWidth:
                                                760,
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t(
                            'name',
                          )}
                        </TableCell>

                        <TableCell>
                          {t(
                            'sku',
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {t(
                            'build.qty',
                            'Qty',
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {t(
                            'build.weight',
                            'Weight',
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {t(
                            'build.volume',
                            'Volume',
                          )}
                        </TableCell>

                        <TableCell>
                          {t(
                            'build.uom',
                          )}
                        </TableCell>

                        <TableCell />
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {draftComponents.map(
                        (
                          component,
                        ) => (
                          <TableRow
                            key={
                              component.id
                            }
                          >
                            <TableCell>
                              {
                                component.componentDescription
                              }
                            </TableCell>

                            <TableCell
                              sx={{
                                fontFamily:
                                                                    'monospace',
                              }}
                            >
                              {
                                component.componentItemNumber
                              }
                            </TableCell>

                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={
                                  tempValues[
                                    `${component.id}-quantity`
                                  ]
                                                                    ?? component.quantity
                                }
                                onChange={(
                                  event,
                                ) =>
                                  handleComponentFieldChange(
                                    component.id,
                                    'quantity',
                                    event
                                      .target
                                      .value,
                                  )
                                }
                                onBlur={() =>
                                  handleComponentFieldBlur(
                                    component.id,
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                  step:
                                                                        'any',
                                  style: {
                                    textAlign:
                                                                            'right',
                                  },
                                }}
                                sx={{
                                  width:
                                                                        90,
                                  ...noSpinnerSx,
                                }}
                              />
                            </TableCell>

                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={
                                  tempValues[
                                    `${component.id}-weight`
                                  ]
                                                                    ?? component.weight
                                                                    ?? ''
                                }
                                onChange={(
                                  event,
                                ) =>
                                  handleComponentFieldChange(
                                    component.id,
                                    'weight',
                                    event
                                      .target
                                      .value,
                                  )
                                }
                                onBlur={() =>
                                  handleComponentFieldBlur(
                                    component.id,
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                  step:
                                                                        'any',
                                  style: {
                                    textAlign:
                                                                            'right',
                                  },
                                }}
                                sx={{
                                  width:
                                                                        90,
                                  ...noSpinnerSx,
                                }}
                              />
                            </TableCell>

                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={
                                  tempValues[
                                    `${component.id}-volume`
                                  ]
                                                                    ?? component.volume
                                                                    ?? ''
                                }
                                onChange={(
                                  event,
                                ) =>
                                  handleComponentFieldChange(
                                    component.id,
                                    'volume',
                                    event
                                      .target
                                      .value,
                                  )
                                }
                                onBlur={() =>
                                  handleComponentFieldBlur(
                                    component.id,
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                  step:
                                                                        'any',
                                  style: {
                                    textAlign:
                                                                            'right',
                                  },
                                }}
                                sx={{
                                  width:
                                                                        90,
                                  ...noSpinnerSx,
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                size="small"
                                value={component.uom}
                                onChange={(event) =>
                                  setDraftComponents((previous) =>
                                    previous.map((item) =>
                                      item.id === component.id
                                        ? { ...item, uom: event.target.value }
                                        : item,
                                    ),
                                  )
                                }
                                onBlur={() =>
                                  handleComponentFieldBlur(
                                    component.id,
                                  )
                                }
                                inputProps={{
                                  'aria-label': `Component UOM ${component.componentItemNumber}`,
                                }}
                                sx={{ width: 90 }}
                              />
                            </TableCell>

                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleRemoveComponent(
                                    component.id,
                                  )
                                }
                              >
                                <Delete
                                  fontSize="small"
                                  color="error"
                                />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </Section>
        )}

        {/* Step 3 */}
        {/* Step 3: Review */}
        {activeStep === 2 && (
          <Section
            style={{
              p: {
                xs: 2,
                sm: 5,
              },
            }}
          >
            <Stack spacing={4}>
              <Typography variant="textXL">
                {t('create.reviewTitle')}
              </Typography>

              <Grid
                container
                spacing={3}
              >
                {[
                  {
                    label: t('name'),
                    value: description,
                  },
                  {
                    label: t(
                      'create.itemNumber',
                    ),
                    value: itemNumber,
                    mono: true,
                  },
                  {
                    label: t(
                      'create.category',
                    ),
                    value: primaryCategory,
                  },
                  {
                    label: t(
                      'yield',
                    ),
                    value:
                                            yieldQuantity === ''
                                              ? '-'
                                              : `${yieldQuantity} portions`,
                  },
                  {
                    label: 'Portion',
                    value:
                                            portionQuantity === ''
                                              ? '-'
                                              : `${portionQuantity} ${portionUom}`,
                  },
                  {
                    label: 'Consumption mode',
                    value: consumptionMode,
                  },
                  {
                    label: 'Expected yield loss',
                    value:
                                            expectedYieldLossPercent === ''
                                              ? '-'
                                              : `${expectedYieldLossPercent}%`,
                  },
                  ...(consumptionMode === 'ProducedStock'
                    ? [
                      {
                        label: 'Production trigger',
                        value: productionTrigger || '-',
                      },
                      {
                        label: 'Storage',
                        value: storageMethod || '-',
                      },
                      {
                        label: 'Shelf life',
                        value:
                                                    shelfLifeValue === ''
                                                      ? '-'
                                                      : `${shelfLifeValue} ${shelfLifeUnit}`,
                      },
                    ]
                    : []),
                  {
                    label: t(
                      'create.productionMethod',
                      'Production Method',
                    ),
                    value:
                                            productionMethods.find(
                                              (pm) =>
                                                pm.id === productionMethodId,
                                            )?.name ?? '-',
                  },
                  {
                    label: t(
                      'create.effectiveDates',
                      'Effective Dates',
                    ),
                    value:
                                            effectiveFrom || effectiveTo
                                              ? `${effectiveFrom || '-'} → ${effectiveTo || '-'
                                              }`
                                              : '-',
                  },

                  {
                    label: 'Narrative description',
                    value:
                                            narrativeDescription || '-',
                    full: true,
                  },
                ].map((field) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={field.full ? 12 : 4}
                    key={field.label}
                  >
                    <Box
                      sx={{
                        p: 3,
                        border: '1px solid',
                        borderColor:
                                                    theme.palette.custom.gray[300],
                        borderRadius: '12px',
                        minHeight: 90,
                      }}
                    >
                      <Box
                        sx={{
                          color:
                                                        theme.palette.custom.gray[600],
                          fontSize: '10px',
                          textTransform: 'uppercase',
                          mb: 0.5,
                          fontWeight: 500,
                        }}
                      >
                        {field.label}
                      </Box>

                      <Box
                        sx={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color:
                                                        theme.palette.custom.gray[900],
                          fontFamily:
                                                        field.mono
                                                          ? 'monospace'
                                                          : undefined,
                          whiteSpace:
                                                        field.full
                                                          ? 'pre-wrap'
                                                          : undefined,
                        }}
                      >
                        {field.value ||
                                                    t('create.none')}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Recipe Image */}
              <Box>
                <Typography
                  variant="textLG"
                  fontWeight={600}
                  sx={{
                    mb: 1,
                  }}
                >
                                    Recipe image
                </Typography>

                {heroImagePreviewUrl ? (
                  <Box
                    component="img"
                    src={heroImagePreviewUrl}
                    alt="Recipe preview"
                    sx={{
                      width: '100%',
                      maxHeight: 260,
                      display: 'block',
                      objectFit: 'cover',
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  />
                ) : (
                  <Typography color="text.secondary">
                                        No recipe image selected.
                  </Typography>
                )}
              </Box>

              {/* Components */}
              <Typography
                variant="textLG"
                fontWeight={600}
              >
                {t('build.componentsTitle')} (
                {draftComponents.length}
                                )
              </Typography>

              <TableContainer
                sx={{
                  overflowX: 'auto',
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: 650,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t('name')}
                      </TableCell>

                      <TableCell>
                        {t('sku')}
                      </TableCell>

                      <TableCell align="right">
                        {t(
                          'build.qty',
                          'Qty',
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {t(
                          'build.weight',
                          'Weight',
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {t(
                          'build.volume',
                          'Volume',
                        )}
                      </TableCell>

                      <TableCell>
                        {t('build.uom')}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {draftComponents.map(
                      (component) => (
                        <TableRow
                          key={component.id}
                        >
                          <TableCell>
                            {
                              component.componentDescription
                            }
                          </TableCell>

                          <TableCell
                            sx={{
                              fontFamily:
                                                                'monospace',
                            }}
                          >
                            {
                              component.componentItemNumber
                            }
                          </TableCell>

                          <TableCell align="right">
                            {component.quantity}
                          </TableCell>

                          <TableCell align="right">
                            {component.weight ?? '—'}
                          </TableCell>

                          <TableCell align="right">
                            {component.volume ?? '—'}
                          </TableCell>

                          <TableCell>
                            {component.uom}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Section>
        )}

        {/* Navigation */}
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={1}
          sx={{
            '& > button': {
              flex: {
                xs: 1,
                sm: 'initial',
              },
            },
          }}
        >
          <Button
            variant="secondary"
            disabled={
              activeStep === 0
            }
            onClick={() =>
              setActiveStep(
                (step) =>
                  step - 1,
              )
            }
          >
            {t(
              'create.previous',
            )}
          </Button>

          {activeStep < 2
            ? (
              <Button
                variant="primary"
                disabled={
                  saving
                                    || (
                                      activeStep
                                        === 0
                                        && !isStep1Valid
                                    )
                                    || (
                                      activeStep
                                        === 1
                                        && pendingComponentSaveIds.size > 0
                                    )
                }
                onClick={() => {
                  if (
                    activeStep
                                        === 0
                  ) {
                    handleStep1Next();
                  } else {
                    setActiveStep(
                      (
                        step,
                      ) =>
                        step
                                                + 1,
                    );
                  }
                }}
              >
                {saving
                  ? '...'
                  : t(
                    'create.next',
                  )}
              </Button>
            )
            : (
              <Button
                variant="primary"
                onClick={
                  handleFinish
                }
                disabled={
                  saving
                }
              >
                {saving
                  ? '...'
                  : t(
                    'create.finish',
                    'Finish',
                  )}
              </Button>
            )}
        </Stack>
      </Stack>
    </PageArea>
  );
};

export default RecipeV2CreatePage;
